import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Grid as Grid2,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { getDashboardSummary, searchPipeline } from "../services/api";
import type { DashboardSummary, DrugCandidate } from "../types";
import PhaseChip from "../components/PhaseChip";
import ProbabilityBar from "../components/ProbabilityBar";

const PIE_COLORS = [
  "#1a237e", "#00897b", "#e65100", "#6a1b9a", "#1565c0",
  "#2e7d32", "#c62828", "#f9a825", "#4527a0", "#00838f",
];

export default function Dashboard() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [topCandidates, setTopCandidates] = useState<DrugCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      try {
        const [summaryData, pipelineData] = await Promise.all([
          getDashboardSummary(),
          searchPipeline({
            sort_by: "probability_of_success",
            sort_dir: "desc",
            page_size: 5,
            show_approved: false,
          }),
        ]);
        setSummary(summaryData);
        setTopCandidates(pipelineData.results);
      } catch (err) {
        console.error("Dashboard load error:", err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <Typography variant="h6" color="text.secondary">
          No data yet. Run your first sync to populate the dashboard.
        </Typography>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate("/pipeline")}
        >
          Go to Pipeline
        </Button>
      </Box>
    );
  }

  const phaseOrder = ["Phase 1", "Phase 2", "Phase 3"];
  const phaseData = phaseOrder
    .filter((p) => summary.by_phase[p] !== undefined)
    .map((name) => ({
      name,
      count: summary.by_phase[name] || 0,
    }));

  const areaData = Object.entries(summary.by_therapeutic_area)
    .slice(0, 8)
    .map(([name, value]) => ({ name, count: value }));

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Dashboard
      </Typography>

      {/* Summary Cards */}
      <Grid2 container spacing={3} sx={{ mb: 4 }}>
        <Grid2 size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <ScienceIcon sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
              <Typography variant="h3" fontWeight={700}>
                {summary.total_candidates.toLocaleString()}
              </Typography>
              <Typography color="text.secondary">
                Injectable Candidates
              </Typography>
            </CardContent>
          </Card>
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <TrendingUpIcon sx={{ fontSize: 40, color: "secondary.main", mb: 1 }} />
              <Typography variant="h3" fontWeight={700}>
                {summary.by_phase["Phase 3"] || 0}
              </Typography>
              <Typography color="text.secondary">
                Phase 3 (Nearest Launch)
              </Typography>
            </CardContent>
          </Card>
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <NotificationsActiveIcon
                sx={{ fontSize: 40, color: "warning.main", mb: 1 }}
              />
              <Typography variant="h3" fontWeight={700}>
                {summary.unread_alerts}
              </Typography>
              <Typography color="text.secondary">Unread Alerts</Typography>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>

      {/* Charts */}
      <Grid2 container spacing={3} sx={{ mb: 4 }}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Pipeline by Phase
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={phaseData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#1a237e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                By Therapeutic Area
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={areaData}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={(props: any) =>
                      `${props.name} ${((props.percent ?? 0) * 100).toFixed(0)}%`
                    }
                    labelLine={false}
                  >
                    {areaData.map((_, idx) => (
                      <Cell
                        key={idx}
                        fill={PIE_COLORS[idx % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>

      {/* Route & Device */}
      <Grid2 container spacing={3} sx={{ mb: 4 }}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                By Route of Administration
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={
                  Object.entries((summary as any).by_route || {}).map(([name, value]) => ({ name, count: value as number }))
                }>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={11} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#00897b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Top Sponsors — SubQ Pipeline
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={
                  Object.entries((summary as any).top_sponsors_subq || {}).slice(0, 10).map(([name, value]) => ({ name, count: value as number }))
                } layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#e65100" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>

      {/* Region Breakdown */}
      <Grid2 container spacing={3} sx={{ mb: 4 }}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Pipeline by Launch Region
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={
                  Object.entries((summary as any).by_region || {})
                    .filter(([name, v]) => (v as number) > 0 && name !== "Other")
                    .sort((a, b) => (b[1] as number) - (a[1] as number))
                    .map(([name, value]) => ({ name, count: value as number }))
                }>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#6a1b9a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                By Delivery Device
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={
                      Object.entries((summary as any).by_device || {})
                        .filter(([name]) => !name.toLowerCase().includes("iv bag"))
                        .slice(0, 8)
                        .map(([name, value]) => ({ name: name.length > 25 ? name.slice(0, 25) + "..." : name, count: value as number }))
                    }
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={(props: any) =>
                      `${props.name} ${((props.percent ?? 0) * 100).toFixed(0)}%`
                    }
                    labelLine={false}
                  >
                    {Object.entries((summary as any).by_device || {}).slice(0, 8).map((_, idx) => (
                      <Cell
                        key={idx}
                        fill={PIE_COLORS[idx % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>

      {/* Administration Stats */}
      {((summary as any).self_administered > 0 || (summary as any).hcp_administered > 0) && (
        <Grid2 container spacing={3} sx={{ mb: 4 }}>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <Card>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h3" fontWeight={700} color="success.main">
                  {((summary as any).self_administered || 0).toLocaleString()}
                </Typography>
                <Typography color="text.secondary">
                  Self-Administered (Patient)
                </Typography>
              </CardContent>
            </Card>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <Card>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h3" fontWeight={700} color="primary.main">
                  {((summary as any).hcp_administered || 0).toLocaleString()}
                </Typography>
                <Typography color="text.secondary">
                  HCP-Administered (Clinic/Hospital)
                </Typography>
              </CardContent>
            </Card>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <Card>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h3" fontWeight={700}>
                  {Object.keys((summary as any).by_route || {}).length}
                </Typography>
                <Typography color="text.secondary">
                  Delivery Routes Tracked
                </Typography>
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>
      )}

      {/* Top Candidates */}
      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">
              Highest Probability Candidates
            </Typography>
            <Button size="small" onClick={() => navigate("/pipeline")}>
              View All
            </Button>
          </Box>
          {topCandidates.map((c) => (
            <Box
              key={c.id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 1.5,
                px: 2,
                borderBottom: "1px solid #f0f0f0",
                cursor: "pointer",
                "&:hover": { backgroundColor: "#fafafa" },
                borderRadius: 1,
              }}
              onClick={() => navigate(`/pipeline/${c.id}`)}
            >
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={600}>{c.drug_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {c.sponsor}
                </Typography>
              </Box>
              <Box sx={{ mx: 2 }}>
                <PhaseChip phase={c.phase} />
              </Box>
              <Box sx={{ mx: 2, minWidth: 100 }}>
                <Typography variant="body2" color="text.secondary">
                  {c.therapeutic_area}
                </Typography>
              </Box>
              <Box sx={{ mx: 2, minWidth: 60 }}>
                <Typography variant="body2" color="text.secondary">
                  {c.projected_launch_year || "--"}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 150 }}>
                <ProbabilityBar value={c.probability_of_success} />
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>
    </Box>
  );
}
